/**
 * Every screen there is, and who may reach it.
 *
 * The guard here is for the look of the thing — an instant redirect instead of
 * a flash of an empty dashboard. It is not the boundary. Every request behind
 * these routes is authorised again by the server, which never trusts anything
 * this file claims about who is holding the phone.
 *
 * `AppDataProvider` sits inside `ProtectedRoute` rather than around it,
 * deliberately: it starts eight reads the moment it mounts, and mounting it
 * around the sign-in screen would fire all eight at a server that is going to
 * answer 401 to each.
 */

import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ProtectedRoute, RequireRole } from './components/ProtectedRoute';
import { AppDataProvider } from './context/AppDataContext';
import { WorkspaceProvider } from './context/WorkspaceContext'
import { useAuth } from './context/AuthContext';
import Board from './pages/Board';
import CalendarPage from './pages/Calendar';
import ClientDetail from './pages/ClientDetail';
import Clients from './pages/Clients';
import Prospects from './pages/Prospects';
import Tasks from './pages/Tasks';
import ContentCalendar from './pages/ContentCalendar';
import Dashboard from './pages/Dashboard';
import NotFound from './pages/NotFound';
import Settings from './pages/Settings';
import Team from './pages/Team';
import TeamDetail from './pages/TeamDetail';
import Documents from './pages/DocumentsPage';
import StudioCanvas from './pages/StudioCanvas';
import { SetPassword, SignIn } from './screens/SignIn';

/**
 * The two branches nobody lands on first.
 *
 * Both are behind the partner guard, so for most of the studio they are code
 * that is downloaded and never run. Splitting them here costs one fetch on the
 * first visit to each and takes them out of what everybody else waits for. The
 * everyday screens stay eager on purpose: a spinner between /today and /board
 * would be a worse trade than the bytes.
 */
const Analytics = lazy(() => import('./pages/Analytics'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminOverview = lazy(() => import('./pages/admin/AdminOverview'));
const AdminActivity = lazy(() => import('./pages/admin/AdminActivity'));
const AdminPermissions = lazy(() => import('./pages/admin/AdminPermissions'));
const AdminKeys = lazy(() => import('./pages/admin/AdminKeys'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
// Write mode opens in a tab of its own, so it is its own download too.
const ContentSheet = lazy(() => import('./pages/ContentSheet'));
// Projects are the long jobs, opened a few times a week rather than hourly.
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
// The same for buckets: looked at when something is being kept or wanted.
const Buckets = lazy(() => import('./pages/Buckets'));
const BucketDetail = lazy(() => import('./pages/BucketDetail'));

/** Held back until the chunk lands. The shell around it is already drawn. */
function Loading() {
  return (
    <div role="status" className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
      <span className="h-7 w-7 animate-spin rounded-full border-2 border-brand-500/25 border-t-brand-500 motion-reduce:animate-none" />
      <span className="text-sm text-shell-text">Loading…</span>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      {/* The two that work while signed out, and the only two. */}
      <Route path="login" element={<SignIn />} />
      <Route path="setup/:token" element={<Setup />} />

      <Route element={<ProtectedRoute />}>
        {/* Full screen, without the shell: a sheet wants every pixel. */}
        <Route
          path="content/sheet"
          element={
            <WorkspaceProvider>
              <AppDataProvider>
                <Suspense fallback={<Loading />}>
                  <ContentSheet />
                </Suspense>
              </AppDataProvider>
            </WorkspaceProvider>
          }
        />
        {/* The client canvas opens from Clients in a tab of its own. */}
        <Route
          path="clients/canvas"
          element={
            <WorkspaceProvider>
              <AppDataProvider>
                <StudioCanvas />
              </AppDataProvider>
            </WorkspaceProvider>
          }
        />
        <Route
          element={
            <WorkspaceProvider>
              <AppDataProvider>
                <AppShell />
              </AppDataProvider>
            </WorkspaceProvider>
          }
        >
          {/* `/today` is what the studio calls the first screen, and what a
              year of "did you check today" means. The route says so even
              though the file behind it is the design's Dashboard. */}
          <Route index element={<Landing />} />
          <Route path="today" element={<Dashboard />} />
          <Route path="board" element={<Board />} />
          <Route path="tasks" element={<Tasks />} />
          <Route
            path="projects"
            element={
              <Suspense fallback={<Loading />}>
                <Projects />
              </Suspense>
            }
          />
          <Route
            path="projects/:id"
            element={
              <Suspense fallback={<Loading />}>
                <ProjectDetail />
              </Suspense>
            }
          />
          <Route
            path="buckets"
            element={
              <Suspense fallback={<Loading />}>
                <Buckets />
              </Suspense>
            }
          />
          <Route
            path="buckets/:id"
            element={
              <Suspense fallback={<Loading />}>
                <BucketDetail />
              </Suspense>
            }
          />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="content" element={<ContentCalendar />} />
          <Route path="clients" element={<Clients />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="prospects" element={<Prospects />} />
          <Route path="people" element={<Team />} />
          <Route path="people/:id" element={<TeamDetail />} />
          <Route path="documents" element={<Documents />} />
          <Route path="settings" element={<Settings />} />

          {/* The month is the money, and the money is the partners'. The
              server strips the figures for anybody else anyway; this only
              saves them a screen that would come back mostly empty. */}
          <Route element={<RequireRole roles={['PARTNER']} />}>
            <Route
              path="month"
              element={
                <Suspense fallback={<Loading />}>
                  <Analytics />
                </Suspense>
              }
            />

            {/* The console. Behind the same guard as the money, because what
                is behind it is the roster and the log of what everybody has
                done — oversight of people rather than of work. The server
                refuses `people.access` and `audit.recent` to anybody else
                regardless of what this file says. */}
            <Route
              path="admin"
              element={
                <Suspense fallback={<Loading />}>
                  <AdminLayout />
                </Suspense>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="team" element={<AdminTeam />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="permissions" element={<AdminPermissions />} />
              <Route path="keys" element={<AdminKeys />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

/** The setup screen wants its token as a prop, and the router has it as a param. */
function Setup() {
  const { token } = useParams<{ token: string }>();
  if (token === undefined || token === '') return <Navigate to="/login" replace />;
  return <SetPassword token={token} />;
}

/** `/` goes wherever the signed-in person asked the app to open, Today by default. */
function Landing() {
  const { currentUser } = useAuth();
  return <Navigate to={`/${currentUser?.preferences.landingPage ?? 'today'}`} replace />;
}
